// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type {
  LinkedLiquidityGate,
  LinkedLiquiditySectionApi,
} from '@client/banks/components/shared/linked-liquidity-section';
import { type TransactionFormValues } from '@client/banks/hooks/use-transaction-form';
import { getBankCurrencySymbol } from '@client/banks/utils/bank-currency';
import { getBankPositionAssetTypeOptions } from '@client/banks/utils/bank-position-asset-type-label';
import {
  getBankTransactionSubtypeOptions,
  getBankTransactionTypeOptions,
} from '@client/banks/utils/bank-transaction-type-label';
import dayjs from 'dayjs';
import { type MutableRefObject, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type TransactionFormOption = {
  value: string;
  label: string;
};

export type TransactionFormProps = {
  formId: string;
  onSubmit: (data: TransactionFormValues) => void | Promise<void>;
  currencyOptions: readonly TransactionFormOption[];
  accountIbanOptions: readonly TransactionFormOption[];
  defaultCurrency: string;
  linkedLiquidityApiRef?: MutableRefObject<LinkedLiquiditySectionApi | null>;
  onLinkedLiquidityGateChange?: (gate: LinkedLiquidityGate) => void;
};

export const useTransactionForm = ({
  currencyOptions,
  accountIbanOptions,
  defaultCurrency,
  linkedLiquidityApiRef,
  onLinkedLiquidityGateChange,
  ..._
}: TransactionFormProps) => {
  const { control, handleSubmit } = useFormContext<TransactionFormValues>();

  const { t } = useTranslation();

  const currency = useWatch({ control, name: 'currency' });

  const currencyPrefix = useMemo(() => getBankCurrencySymbol(currency), [currency]);

  const todayMaxDate = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const transactionTypeOptions = useMemo(
    () => getBankTransactionTypeOptions(t),
    [t],
  );

  const subtypeOptions = useMemo(() => getBankTransactionSubtypeOptions(t), [t]);

  const assetTypeOptions = useMemo(() => getBankPositionAssetTypeOptions(t), [t]);

  return {
    control,
    handleSubmit,
    t,
    currencyPrefix,
    todayMaxDate,
    transactionTypeOptions,
    subtypeOptions,
    assetTypeOptions,
    currencyOptions,
    accountIbanOptions,
    defaultCurrency,
    linkedLiquidityApiRef,
    onLinkedLiquidityGateChange,
  };
};
